import React from 'react';
import { Box, Button, Link as MuiLink, Stack, Typography } from '@mui/material';
import { AutoAwesome as SparkleIcon, Check as CheckIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { tokens } from '../../../theme/tokens';
import { FieldLabel, Pill, ToolButton } from './primitives';
import type { RepairToolId, ToolResult } from './productDetailTypes';

export const REPAIR_TOOLS: Array<{ id: RepairToolId; label: string; help: string; platformAdmin?: boolean }> = [
  { id: 'verify', label: 'AI verify', help: 'Check every field against the brand page and flag mismatches' },
  { id: 'scrape', label: 'Scrape page', help: 'Re-read the manufacturer page; the result lands in Staged products for review', platformAdmin: true },
  { id: 'market-pricing', label: 'Market pricing', help: 'Pull street prices from authorized dealers' },
  { id: 'enrich', label: 'Enrich IDs', help: 'Look up UPC / EAN / GTIN / MPN' },
  { id: 'weight', label: 'Re-extract weight', help: 'Read product and shipping weight from source' },
  { id: 'launch-dates', label: 'Launch dates', help: 'Find announce and ship dates' },
  { id: 'alt-text', label: 'Write alt text', help: 'Describe each photo from what’s in it' },
  { id: 'backfill', label: '3P backfill', help: 'Fill empty fields from third-party catalogs (writes only fields that are empty)', platformAdmin: true },
];

export type Suggestion = Extract<ToolResult, { kind: 'suggestions' }>;

export interface SessionLogEntry {
  id: number;
  text: string;
  tone: 'positive' | 'neutral';
  href?: string;
}

interface Props {
  issues: string[];
  recommended: RepairToolId[];
  running: Partial<Record<RepairToolId, boolean>>;
  suggestions: Suggestion[];
  log: SessionLogEntry[];
  lastVerify?: string;
  editing: boolean;
  accepting: boolean;
  onRun: (tool: RepairToolId) => void;
  onAccept: (tools: RepairToolId[]) => void;
  onDismiss: (tools: RepairToolId[]) => void;
}

const toolLabel = (id: RepairToolId) => REPAIR_TOOLS.find((t) => t.id === id)?.label ?? id;

export const RepairEnrichCard: React.FC<Props> = ({ issues, recommended, running, suggestions, log, lastVerify, editing, accepting, onRun, onAccept, onDismiss }) => {
  const anyRunning = Object.values(running).some(Boolean);
  const healthLine = anyRunning
    ? 'Running — results show here as suggestions.'
    : issues.length
      ? `${issues[0].charAt(0).toUpperCase()}${issues[0].slice(1)}${issues.length > 1 ? `; ${issues.slice(1).join('; ')}` : ''}. Tools with a dot fix these.`
      : 'No open issues. Run a tool any time to refresh data from source.';

  return (
    <Box sx={{ border: `1px solid ${tokens.line.default}`, bgcolor: tokens.surface.card, borderRadius: `${tokens.radius.card}px`, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: '18px', pt: '14px', pb: 1.25, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <SparkleIcon sx={{ fontSize: 16, color: tokens.accent.main }} />
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>Repair &amp; enrich</Typography>
            {issues.length ? <Pill tone="caution">{issues.length} to fix</Pill> : <Pill tone="positive">Healthy</Pill>}
          </Stack>
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{healthLine}</Typography>
        </Box>
        {lastVerify && <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint, pt: '3px' }}>Last AI verify {lastVerify}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', gap: '7px', flexWrap: 'wrap', px: '18px', pb: '14px' }}>
        {REPAIR_TOOLS.map((tool) => (
          <ToolButton
            key={tool.id}
            tooltip={editing ? 'Save or discard your edits first' : tool.help}
            running={!!running[tool.id]}
            recommended={recommended.includes(tool.id)}
            disabled={editing}
            onClick={() => onRun(tool.id)}
          >
            {running[tool.id] ? `${tool.label}…` : tool.label}
          </ToolButton>
        ))}
      </Box>

      {suggestions.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${tokens.line.hairline}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: '18px', py: 1.25, bgcolor: tokens.signal.info.row, flexWrap: 'wrap' }}>
            <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.accent.hover }}>
              {suggestions.length === 1 ? '1 suggestion' : `${suggestions.length} suggestions`}
            </Typography>
            <Typography component="span" sx={{ flex: '1 1 200px', fontSize: 12, color: tokens.ink.bodyAlt }}>
              Nothing changes until you accept. Accepted fields are saved as AI and kept through future staged and automatic updates.
            </Typography>
            <Button size="small" variant="contained" disabled={accepting} onClick={() => onAccept(suggestions.map((s) => s.tool))} sx={{ height: 28, borderRadius: '7px', fontSize: 12, fontWeight: 600 }}>
              Accept all
            </Button>
            <Button size="small" variant="outlined" onClick={() => onDismiss(suggestions.map((s) => s.tool))} sx={{ height: 28, borderRadius: '7px', fontSize: 12, fontWeight: 600, color: tokens.ink.body, borderColor: tokens.line.default }}>
              Dismiss all
            </Button>
          </Box>
          {suggestions.map((s) => (
            <Box key={s.tool} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: '18px', py: 1.5, borderTop: `1px solid ${tokens.line.hairline}`, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                  <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{s.title}</Typography>
                  <Box component="span" sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', px: '5px', py: '1px', borderRadius: '4px', border: `1px solid ${tokens.signal.info.border}`, bgcolor: tokens.signal.info.tint, color: tokens.signal.info.ink, textTransform: 'uppercase' }}>
                    {toolLabel(s.tool)}
                  </Box>
                  {s.confidence && <Typography component="span" sx={{ fontSize: 11.5, color: tokens.ink.faint }}>{s.confidence}</Typography>}
                </Stack>
                {s.rows.map((r, i) => (
                  <Box key={`${r.path}-${i}`} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(80px, 140px) minmax(0, 1fr)' }, gap: 1.25, fontSize: 12.5, lineHeight: 1.5 }}>
                    <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.muted, overflowWrap: 'anywhere' }}>{r.label}</Typography>
                    <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'baseline', minWidth: 0 }}>
                      {r.from && <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.faint, textDecoration: 'line-through', overflowWrap: 'anywhere' }}>{r.from}</Typography>}
                      <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.primary, overflowWrap: 'anywhere' }}>{r.to}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Stack direction="row" spacing={0.75}>
                <Button size="small" variant="outlined" disabled={accepting} onClick={() => onAccept([s.tool])} sx={{ height: 28, borderRadius: '7px', fontSize: 12, fontWeight: 600, borderColor: tokens.accent.border, color: tokens.accent.hover }}>
                  Accept
                </Button>
                <Button size="small" onClick={() => onDismiss([s.tool])} sx={{ height: 28, borderRadius: '7px', fontSize: 12, fontWeight: 600, color: tokens.ink.muted }}>
                  Dismiss
                </Button>
              </Stack>
            </Box>
          ))}
        </Box>
      )}

      {log.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: '18px', pt: 1.25, pb: 1.5, borderTop: `1px solid ${tokens.line.hairline}` }}>
          <FieldLabel>This session</FieldLabel>
          {log.slice(0, 4).map((entry) => (
            <Stack key={entry.id} direction="row" alignItems="baseline" spacing={1}>
              <CheckIcon sx={{ fontSize: 12, color: entry.tone === 'positive' ? tokens.signal.positive.solid : tokens.ink.faint, alignSelf: 'center' }} />
              <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.body }}>{entry.text}</Typography>
              {entry.href && (
                <MuiLink component={RouterLink} to={entry.href} underline="hover" sx={{ fontSize: 12.5, fontWeight: 600 }}>
                  View job
                </MuiLink>
              )}
              <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.faint }}>just now</Typography>
            </Stack>
          ))}
        </Box>
      )}
    </Box>
  );
};
